import { ITEM_W, ITEM_GAP } from './canvas/autoLayout.js'

// Pure builders for a starter template's canvas-tile content — mirrors
// blank's own components+canvas.items shape (FEATURED_TEMPLATES[0] below)
// instead of leaving mobile/app/service canvas-blank. `pages` stays the
// served-app route stubs; these derive the SEPARATE canvas-preview
// component set from that same list, so the two can never drift apart.
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)

// A template's real content pages — every `pages` entry that carries its
// own `text` (excludes an `index: { extends: '<home>' }` route alias, which
// has no separate tile of its own).
const realPageEntries = (pages) =>
  Object.entries(pages)
    .filter(([, def]) => typeof def.text === 'string')
    .map(([key, def]) => [key, def.text])

const templateNav = (entries) =>
  Object.fromEntries(
    entries.map(([key, text]) => [cap(key) + 'Link', { extends: 'Link', href: '/' + key, text }])
  )

const templateComponents = (entries) =>
  Object.fromEntries(
    entries.map(([key, text]) => [
      key,
      { Heading: { tag: 'h1', text }, Nav: { flow: 'row', ...templateNav(entries) } }
    ])
  )

// Same single-cluster grid math as canvas/autoLayout.js (ITEM_W/ITEM_GAP,
// alphabetical within the cluster) — every template here has ≤4 real pages
// and no '/'-prefixed route keys, so it always resolves to one row.
const templateCanvasItems = (entries) => {
  const sorted = [...entries].sort(([a], [b]) => a.localeCompare(b))
  return Object.fromEntries(
    sorted.map(([key], i) => [
      key,
      { x: i * (ITEM_W + ITEM_GAP), y: 0, w: ITEM_W, h: ITEM_W, pageKey: key }
    ])
  )
}

const MOBILE_PAGES = {
  index: { extends: 'home' },
  home: { tag: 'div', text: 'Home' },
  settings: { tag: 'div', text: 'Settings' }
}
const MOBILE_REAL_PAGES = realPageEntries(MOBILE_PAGES)

const APP_PAGES = {
  index: { extends: 'dashboard' },
  dashboard: { tag: 'div', text: 'Dashboard' },
  settings: { tag: 'div', text: 'Settings' },
  login: { tag: 'div', text: 'Login' }
}
const APP_REAL_PAGES = realPageEntries(APP_PAGES)

// THE APP SPLIT (docs/APP_SPLIT.md): this modal creates the things you
// PUBLISH — websites first. A website starts with the three pages every site
// has; a workspace app is never created here (that is /add-app).
const WEBSITE_PAGES = {
  index: { extends: 'home' },
  home: { tag: 'div', text: 'Home' },
  about: { tag: 'div', text: 'About' },
  contact: { tag: 'div', text: 'Contact' }
}
const WEBSITE_REAL_PAGES = realPageEntries(WEBSITE_PAGES)

const SERVICE_PAGES = {
  index: { extends: 'overview' },
  overview: { tag: 'div', text: 'Overview' },
  api: { tag: 'div', text: 'API' },
  health: { tag: 'div', text: 'Health' }
}
const SERVICE_REAL_PAGES = realPageEntries(SERVICE_PAGES)

export const FEATURED_TEMPLATES = [
  {
    id: 'blank',
    // `label`/`hint` hold bundle KEYS — NpTemplateCard resolves them through
    // polyglot (`text: (el, s) => s._tplLabel` reads them off state).
    label: 'canvas.newProject.templateBlank.label',
    hint: 'canvas.newProject.templateBlank.hint',
    icon: 'plus',
    // An empty WEBSITE (the app split: everything this modal makes is published).
    projectType: 'website',
    pages: {},
    components: {
      home: { Heading: { tag: 'h1', text: 'Hello World' } }
    },
    canvas: {
      showDesignSystem: false,
      initialFocus: 'home',
      items: { home: { x: 0, y: 0, w: 1722, h: 1722, pageKey: 'home' } }
    }
  },
  {
    id: 'website',
    label: 'canvas.newProject.templateWebsite.label',
    hint: 'canvas.newProject.templateWebsite.hint',
    icon: 'globe',
    projectType: 'website',
    pages: WEBSITE_PAGES,
    components: templateComponents(WEBSITE_REAL_PAGES),
    canvas: { items: templateCanvasItems(WEBSITE_REAL_PAGES) }
  },
  {
    id: 'mobile',
    label: 'canvas.newProject.templateMobile.label',
    hint: 'canvas.newProject.templateMobile.hint',
    icon: 'deviceMobile',
    projectType: 'mobile',
    pages: MOBILE_PAGES,
    components: templateComponents(MOBILE_REAL_PAGES),
    canvas: { items: templateCanvasItems(MOBILE_REAL_PAGES), previewSize: 'mobileXS' }
  },
  {
    id: 'app',
    label: 'canvas.newProject.templateApp.label',
    hint: 'canvas.newProject.templateApp.hint',
    icon: 'deviceSmallScreen',
    projectType: 'app',
    pages: APP_PAGES,
    components: templateComponents(APP_REAL_PAGES),
    canvas: { items: templateCanvasItems(APP_REAL_PAGES) }
  },
  {
    id: 'deck',
    label: 'canvas.newProject.templateDeck.label',
    hint: 'canvas.newProject.templateDeck.hint',
    icon: 'play',
    projectType: 'deck',
    templateProjectKey: 'commercials/deck',
    pages: {
      index: { extends: 'slide0' },
      slide0: { tag: 'div', text: 'Slide 1' },
      slide1: { tag: 'div', text: 'Slide 2' },
      slide2: { tag: 'div', text: 'Slide 3' },
      slide3: { tag: 'div', text: 'Slide 4' },
      slide4: { tag: 'div', text: 'Slide 5' },
      slide5: { tag: 'div', text: 'Slide 6' },
      slide6: { tag: 'div', text: 'Slide 7' },
      slide7: { tag: 'div', text: 'Slide 8' },
      slide8: { tag: 'div', text: 'Slide 9' },
      slide9: { tag: 'div', text: 'Slide 10' },
      slide10: { tag: 'div', text: 'Slide 11' },
      slide11: { tag: 'div', text: 'Slide 12' },
      slide12: { tag: 'div', text: 'Slide 13' }
    }
  },
  {
    id: 'library',
    label: 'canvas.newProject.templateLibrary.label',
    hint: 'canvas.newProject.templateLibrary.hint',
    icon: 'layers',
    projectType: 'library',
    pages: null
  },
  {
    id: 'service',
    label: 'canvas.newProject.templateService.label',
    hint: 'canvas.newProject.templateService.hint',
    icon: 'api',
    projectType: 'app',
    pages: SERVICE_PAGES,
    components: templateComponents(SERVICE_REAL_PAGES),
    canvas: { items: templateCanvasItems(SERVICE_REAL_PAGES) }
  }
]

export const projectDataChangesForTemplate = (template) => [
  ['set', ['pages'], template.pages || {}],
  ['set', ['components'], template.components || {}],
  ['set', ['canvas'], template.canvas || {}]
]

const hasExpectedProjectData = (snapshot) => {
  const data = snapshot?.data || snapshot || {}
  return (
    Object.keys(data.pages || {}).length === 0 &&
    Object.keys(data.components || {}).length === 1 &&
    !!data.components?.home
  )
}

export async function replaceGeneratedProjectData(sdk, projectId, template) {
  const changes = projectDataChangesForTemplate(template)
  for (let attempt = 0; attempt < 3; attempt++) {
    await sdk.applyProjectChanges(projectId, changes, {
      message: 'Initialize blank project'
    })
    const snapshot = await sdk.getProjectData(projectId)
    if (hasExpectedProjectData(snapshot)) return snapshot
    if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)))
  }
  throw new Error('Blank project initialization did not persist')
}
